import { useState, useEffect } from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { getSimilarityRadar } from '../../services/api';
import styles from './SimilarityLabPage.module.css';

// Fixed categorical order (validated for the dark surface): main driver wears
// the brand accent; overlays take the next slots in order, never re-cycled.
export const SERIES_COLORS = ['#e8411a', '#3b82f6', '#059669', '#d97706'];

// A ±2·SE percentile band narrower than this reads as measurement noise —
// only wider bands are shown (they flag low-pass-count entities).
const RANGE_MIN_WIDTH = 3;

// Per-axis explanations (native title tooltips — repo convention, cf.
// WeightSliders). `short` rides in the chart tooltip; `short` + `calc`
// together form the hover on the axis-table row. Text mirrors what
// corner_vector_read.AXES / _raw() actually compute — update both together.
export const AXIS_INFO = {
  braking_aggression: {
    short: 'Peak braking force, as a fraction of the session’s own grip ceiling.',
    calc: 'Raw = the average, over every clean corner pass, of the peak '
        + 'deceleration in that corner divided by the session’s peak combined G. '
        + 'Self-normalized per session, so tire and car grip differences cancel. '
        + 'High = hitting the brakes hard, near the car’s limit; low = gentle '
        + 'initial application.',
  },
  trail_braking: {
    short: 'How long the brakes stay on while cornering load builds.',
    calc: 'Raw = the average fraction of each corner’s braking phase where '
        + 'deceleration overlaps with rising lateral G — i.e. brake carried '
        + 'past turn-in toward the apex rather than released in a straight line.',
  },
  brake_turn_overlap: {
    short: 'How seamlessly braking hands over to cornering (friction-circle dip at turn-in).',
    calc: 'Raw = the average depth of the combined-G “valley” between brake '
        + 'release and full cornering load — a deep dip means the car briefly '
        + 'uses neither braking nor cornering grip. Scored inverted: further out '
        + 'on the radar = shallower dip = no dead spot at turn-in.',
  },
  corner_commitment: {
    short: 'Minimum corner speed carried, ranked against the field corner by corner.',
    calc: 'For each corner, the driver’s average vMin is percent-ranked against '
        + 'every other driver + car in that same corner; the axis averages '
        + 'those ranks across all corners. Ranking within each corner keeps a '
        + 'hairpin from competing with a kink. Unlike the other axes, the raw '
        + 'value here is already a 0–1 rank, not a physical quantity.',
  },
  throttle_discipline: {
    short: 'How little time is spent coasting — neither braking nor accelerating.',
    calc: 'Raw = the average fraction of each corner window spent coasting, '
        + 'derived from GPS deceleration (works without a throttle sensor). '
        + 'Scored inverted: further out = less dead time between brake release '
        + 'and throttle — crisper transitions.',
  },
  consistency: {
    short: 'How repeatable the speed profile and driven line are, pass after pass.',
    calc: 'Every pass is compared to the driver’s own average speed + line '
        + 'shape for that corner; raw = the mean distance from that personal '
        + 'average. Scored inverted: further out = passes group tighter around '
        + 'their own pattern.',
  },
  smoothness: {
    short: 'Steering fluidity relative to how hard the car is being driven.',
    calc: 'Raw = lap-level steering smoothness divided by peak combined G, '
        + 'averaged over leaderboard laps. Needs yaw/steering data — blank when '
        + 'the logger doesn’t provide it. Scored inverted: further out = calmer '
        + 'hands at the same grip level.',
  },
  rotation: {
    short: 'How much the car is rotated (yawed) beyond its driven line — oversteer bias.',
    calc: 'Raw = the leaderboard oversteer index averaged over laps: yaw rate in '
        + 'excess of what the driven line requires. Needs yaw data — blank when '
        + 'unlogged. High = rotating the car with attitude; low = the car tracks '
        + 'its line neutrally or pushes.',
  },
};

const RADAR_METHOD_TIP =
  'How every axis is computed: the driver’s average over all of their clean '
  + 'corner passes at this track (with this car) is converted to a midrank '
  + 'percentile against every driver with at least min-passes clean passes — '
  + 'each of a driver’s cars counts separately. 50 = field median. Axes where '
  + 'a smaller raw value means more of the named trait (brake-turn overlap, '
  + 'throttle discipline, consistency, smoothness) are flipped, so further '
  + 'out always means more of the trait.\n\nClean passes exclude out/in laps, '
  + 'laps >110% of the '
  + 'session best, and anomalously slow corner passes. The Laps control above '
  + 'further restricts everyone (driver and field) to each session’s '
  + 'best-N laps by lap time.';

export function ordinal(n) {
  const v = Math.round(n);
  const suffix = (v % 100 >= 11 && v % 100 <= 13) ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' }[v % 10] || 'th');
  return `${v}${suffix}`;
}

export function fmtPctl(a) {
  if (!a || a.value == null) return '—';
  const pt = ordinal(a.value);
  if (a.value_lo != null && a.value_hi - a.value_lo >= RANGE_MIN_WIDTH) {
    return `${pt} (${a.value_lo.toFixed(0)}–${a.value_hi.toFixed(0)})`;
  }
  return pt;
}

function AxisTooltip({ active, payload, meta }) {
  if (!active || !payload?.length) return null;
  const key = payload[0]?.payload?.key;
  const m = meta[key];
  return (
    <div className={styles.tip}>
      <div className={styles.tipTitle}>{payload[0]?.payload?.axis}</div>
      {AXIS_INFO[key] && <div className={styles.tipDesc}>{AXIS_INFO[key].short}</div>}
      {payload.map(p => (
        <div key={p.name} className={styles.tipRow}>
          <i style={{ background: p.stroke || p.color }} />
          <span>{p.name}</span>
          <strong>{p.value == null ? 'n/a' : ordinal(p.value)}</strong>
        </div>
      ))}
      {m && (
        <div className={styles.tipFoot}>
          raw {m.raw ?? '—'} · field median {m.field_median ?? '—'} · n={m.n_field}
          {m.value_lo != null && m.value_hi - m.value_lo >= RANGE_MIN_WIDTH && (
            <> · 95% range {m.value_lo.toFixed(0)}–{m.value_hi.toFixed(0)}</>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Eight-axis technique radar: each axis is the driver's percentile vs the
 * track's field (see corner_vector_read.AXES), so a bigger polygon always
 * means "more of the labeled trait". Overlay drivers come from the Similar
 * Drivers tab's checkboxes.
 */
export default function DriverRadar({ trackId, viewUserId, vehicleId, overlayKeys, lapBasis, minPasses, onMinPasses }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!trackId || vehicleId == null) { setData(null); return; }   // '' is a valid (unattributed) car
    let cancelled = false;
    getSimilarityRadar(trackId, { userId: viewUserId, vehicleId, minPasses, overlayKeys, lapBasis })
      .then(d => !cancelled && (setData(d), setError(null)))
      .catch(e => !cancelled && setError(String(e.message || e)));
    return () => { cancelled = true; };
  }, [trackId, viewUserId, vehicleId, minPasses, overlayKeys, lapBasis]);

  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return <div className={styles.empty}>Pick a track…</div>;
  if (!data.axes.length || data.n_passes === 0) {
    return <div className={styles.empty}>No corner-vector data for this driver + car at this track.</div>;
  }

  const meName = data.label;   // "Driver · Car" entity label
  const meta = Object.fromEntries(data.axes.map(a => [a.key, a]));
  const chartData = data.axes.map(a => {
    const row = { axis: a.label, key: a.key, [meName]: a.value };
    for (const ov of data.overlays) {
      row[ov.label] = ov.axes.find(x => x.key === a.key)?.value ?? null;
    }
    return row;
  });
  const series = [meName, ...data.overlays.map(o => o.label)];
  const missing = data.axes.filter(a => a.value == null).map(a => a.label);

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <span title={RADAR_METHOD_TIP}>
          {meName} — technique percentiles vs {data.field_size} driver{data.field_size === 1 ? '' : 's'}
          <span className={styles.muted}> · {data.n_passes.toLocaleString()} clean passes</span>
          {data.below_min_passes && (
            <span className={styles.warn}> · below the {data.min_passes}-pass field threshold</span>
          )}
        </span>
        <label className={styles.minPasses}
               title={'Minimum clean corner passes a driver (with a given car) '
                    + 'needs to be counted in the percentile field. Raise it to '
                    + 'compare only against well-sampled drivers (steadier '
                    + 'percentiles, smaller field); lower it to include everyone '
                    + '(noisier). The viewed driver is always shown, even below '
                    + 'the threshold.'}>
          min passes
          <input
            type="number" min={2} max={500} value={minPasses}
            onChange={e => onMinPasses(Math.max(2, Number(e.target.value) || 2))}
          />
        </label>
      </div>

      <div className={styles.legendRow}>
        {series.map((name, i) => (
          <span key={name} className={styles.legendChip}>
            <i style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }} />{name}
          </span>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={420}>
        <RadarChart data={chartData} accessibilityLayer={false} margin={{ top: 12, right: 40, bottom: 12, left: 40 }}>
          <PolarGrid stroke="#2c2c2c" />
          <PolarAngleAxis dataKey="axis" tick={{ fill: '#a8a8a8', fontSize: 11 }} />
          <PolarRadiusAxis domain={[0, 100]} tick={{ fill: '#787878', fontSize: 10 }} tickCount={5} />
          <Tooltip content={<AxisTooltip meta={meta} />} />
          {series.map((name, i) => (
            <Radar
              key={name} name={name} dataKey={name}
              stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
              strokeWidth={i === 0 ? 2.5 : 1.8}
              fill={SERIES_COLORS[i % SERIES_COLORS.length]}
              fillOpacity={i === 0 ? 0.22 : 0.07}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </RadarChart>
      </ResponsiveContainer>

      {missing.length > 0 && (
        <div className={styles.hint}>
          n/a (missing channels): {missing.join(', ')}
        </div>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th title="Hover an axis name below for what it measures and how it’s computed.">
              Axis
            </th>
            <th title={'Rank of this driver\'s average vs the field, one vote per '
                     + 'driver + car. A range in parentheses is the ±2·SE '
                     + '(~95%) band — where the rank could plausibly sit given how '
                     + 'few passes this driver has. High-volume drivers have no '
                     + 'visible range; treat wide ranges as "estimate, not fact".'}>
              Percentile
            </th>
            <th title={'The driver\'s actual average for the axis, in its native '
                     + 'unit — mostly fractions of grip or of the corner window; '
                     + 'consistency is a shape distance; corner vMin is '
                     + 'already a 0–1 rank. Hover the axis name for the formula.'}>
              Raw
            </th>
            <th title={'The raw value of the middle driver in the field — the '
                     + '50th-percentile reference this driver is measured against.'}>
              Field median
            </th>
            <th title={'How many drivers (with ≥ min passes, each car counted '
                     + 'separately) have data for this axis. Percentiles on a '
                     + 'small field are coarse — with 5 drivers each rank step '
                     + 'is 20 points.'}>
              Field
            </th>
          </tr>
        </thead>
        <tbody>
          {data.axes.map(a => {
            const info = AXIS_INFO[a.key];
            return (
              <tr key={a.key}>
                <td title={info ? `${info.short}\n\n${info.calc}` : undefined}>{a.label}</td>
                <td>{fmtPctl(a)}</td>
                <td>{a.raw == null ? '—' : a.raw}</td>
                <td>{a.field_median == null ? '—' : a.field_median}</td>
                <td>{a.n_field}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
